'use client';

import { type FC, useState, useContext } from 'react';
import {
  exportConversationAsCSV,
  exportConversationAsPDF,
} from '@/_api/admin-conversations';
import type { ExportConversationDialogProps } from './ExportConversationDialog.types';
import {
  Dialog,
  Flex,
  Select,
  Button,
  ButtonGroup,
  Separator,
} from '@/_components';
import { UserContext } from '@/_contexts/UserProvider';
import { AdminConversationsContext } from '@/_contexts/admin/AdminConversationsProvider';
import { ToastContext } from '@/_contexts/ToastProvider';
import { TIMEFRAMES, EXPORT_FORMAT_OPTIONS } from '@/_constants/conversations';
import { getExportTimestamp } from '@/_utils/conversations';

export const ExportConversationDialog: FC<ExportConversationDialogProps> = ({
  isOpen,
  onClose,
}) => {
  const { addToast } = useContext(ToastContext);
  const { token } = useContext(UserContext);
  const { selectedMessage, onToggleExporting } = useContext(
    AdminConversationsContext,
  );

  const [selectedFormat, setSelectedFormat] = useState<string>(
    EXPORT_FORMAT_OPTIONS[0]?.value || '',
  );
  const [selectedTimestamp, setSelectedTimestamp] = useState<string>(
    TIMEFRAMES?.[0]?.value || '',
  );

  const onSelectTimestamp = (event: React.ChangeEvent<HTMLSelectElement>) =>
    setSelectedTimestamp(event.target.value);
  const onSelectFormat = (event: React.ChangeEvent<HTMLSelectElement>) =>
    setSelectedFormat(event.target.value);

  const onExport = async () => {
    const { start_date, end_date } = getExportTimestamp(selectedTimestamp);

    try {
      if (selectedFormat === 'CSV') {
        await exportConversationAsCSV({
          conversationId: selectedMessage?.id!,
          start_date,
          end_date,
          token: token!,
        });
      } else {
        await exportConversationAsPDF({
          conversationId: selectedMessage?.id!,
          start_date,
          end_date,
          token: token!,
        });
      }

      addToast(
        'success',
        selectedFormat === 'CSV'
          ? 'Conversation exported successfully'
          : 'Conversation exported successfully, you will get a notification when it is ready',
      );
    } catch (error) {
      addToast('error', null, { error });
    } finally {
      onToggleExporting();
    }
  };

  return (
    <Dialog isOpen={isOpen} onClose={onClose} title="Export Conversation">
      <Dialog.Content>
        <Flex isColumn={true} gap={8}>
          <Flex gap={8}>
            <Select
              items={EXPORT_FORMAT_OPTIONS}
              isDisabled={false}
              label="Format"
              onChange={onSelectFormat}
              selectedValue={selectedFormat}
            />
            <Select
              items={TIMEFRAMES}
              label="Timeframe"
              isDisabled={false}
              onChange={onSelectTimestamp}
              selectedValue={selectedTimestamp}
            />
          </Flex>
          <Separator isLight={true} />
          <ButtonGroup align="right">
            <Button
              value="Cancel"
              intent="secondary"
              onClick={onToggleExporting}
            />
            <Button value="Export" intent="primary" onClick={onExport} />
          </ButtonGroup>
        </Flex>
      </Dialog.Content>
    </Dialog>
  );
};
